import {
  dimensionSelector,
  pullToRefreshSelector,
} from '@common/redux/selectors/page';
import { useLocation } from '@common/routes/hooks';
import { ElementType, IPage } from '@common/types';
import {
  getKeyboardAvoidingViewBehavior,
  getPaddingBottomScreen,
} from '@common/utils/screen';
import { default as queryString, ParsedQuery } from 'query-string';
import React, { useLayoutEffect, useMemo, useRef } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  RefreshControl,
  ScrollView,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import ObjectRender from '../ObjectRender';
import { setPullToRefresh } from '@common/redux/slice/page';
import { IBreakpoint } from '@nocode/types';
import { appInfoSelector } from '@common/redux/selectors/app';
import { AppVersion } from 'click-types';
import { previousLocationSelector } from '@common/redux/selectors/locationHistory';

type Props = {
  arrNormal: ElementType[];
  screen: IPage;
};

function NormalComponent({ arrNormal, screen }: Props): JSX.Element {
  const appInfo = useSelector(appInfoSelector);
  const dispatch = useDispatch();
  const isV4 = appInfo?.version === AppVersion.V4;

  const ref: any = useRef();

  const { search } = useLocation();
  const previousLocation = useSelector(previousLocationSelector);
  const refreshing = useSelector(pullToRefreshSelector);

  const onRefresh = React.useCallback(() => {
    dispatch(setPullToRefresh(true));
    const timer = setTimeout(() => {
      dispatch(setPullToRefresh(false));
    }, 1500);
    return () => clearTimeout(timer);
  }, [dispatch]);

  const dimension = useSelector(dimensionSelector);

  const pullToRefresh = useMemo(
    () => screen?.attributes?.pullToRefresh || false,
    [screen]
  );

  const originY = dimension.height / screen.height;

  let paddingBottom = getPaddingBottomScreen(screen, originY);

  const { hiddenStatusBar, targetModal }: ParsedQuery<any> = useMemo(
    () => queryString.parse(search),
    [search]
  );
  const { targetModal: previousTargetModal }: ParsedQuery<any> = useMemo(
    () => queryString.parse(previousLocation?.search || ''),
    [previousLocation?.search]
  );

  const isShowStatusBar =
    !screen.isModal &&
    !(Platform.OS === 'android' || Platform.OS === 'ios') &&
    !hiddenStatusBar &&
    screen?.attributes?.statusBarStyle !== 'hidden';

  // Forward breakpoint padding so percent-width children resolve correctly.
  const breakpointStyle =
    isV4 && screen.autoLayout && screen.type === 'breakpoint'
      ? ((screen as unknown as IBreakpoint)?.style as any) || {}
      : {};
  const breakpointPadding = Number(breakpointStyle?.padding || 0);
  const parentPaddingLeft = Number(
    breakpointStyle?.paddingLeft ??
      breakpointStyle?.paddingHorizontal ??
      breakpointPadding
  );
  const parentPaddingRight = Number(
    breakpointStyle?.paddingRight ??
      breakpointStyle?.paddingHorizontal ??
      breakpointPadding
  );

  // scroll to top when modal is opened
  useLayoutEffect(() => {
    const previousLocationIsModal = !!previousTargetModal;
    if (!targetModal && !previousLocationIsModal) {
      ref?.current?.scrollTo({ y: 0, x: 0, animated: false });
    }
  }, [screen?.screenId, targetModal]);

  return (
    <KeyboardAvoidingView
      behavior={getKeyboardAvoidingViewBehavior()}
      style={{ flex: 1 }}
      // keyboardVerticalOffset={50}
      nativeID="render-dynamic-object"
    >
      <ScrollView
        nativeID="render-dynamic-object-scroll"
        bounces={pullToRefresh}
        contentInsetAdjustmentBehavior="automatic"
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        ref={ref}
        style={
          Platform.OS !== 'web'
            ? { flex: 1, paddingBottom: paddingBottom }
            : { flex: 1 }
        }
        contentContainerStyle={{
          position: 'relative',
          ...(!isV4 ? { flexGrow: 1 } : {}),
          paddingBottom: paddingBottom,
          ...(screen.screenTemplate && screen.screenTemplate != 'component'
            ? {
                display: 'flex',
                alignItems: 'center',
              }
            : {}),

          // v4-responsive
          ...(isV4 && screen.autoLayout && screen.type === 'breakpoint'
            ? {
                ...(screen as unknown as IBreakpoint)?.style,
                width: '100%',
                display: 'flex',
              }
            : {}),
        }}
        {...(Platform.OS === 'android' && { overScrollMode: 'never' })}
        {...(pullToRefresh && {
          refreshControl: (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              progressViewOffset={35}
            />
          ),
        })}
      >
        {arrNormal.length > 0 && (
          <ObjectRender
            arrComp={arrNormal}
            isScreen={true}
            isShowStatusBar={isShowStatusBar}
            screen={screen}
            layout={{
              parentWidth: dimension.width,
              parentPaddingLeft,
              parentPaddingRight,
            }}
          />
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

export default NormalComponent;
